<template>
  <v-sheet border>
    <v-locale-provider locale="de">
      <v-data-table-server
          v-model:options="paginationOptions"
          v-model:sort-by="sortBy"
          :headers="props.displayHeader"
          :items="list"
          :items-length="totalItems"
          :items-per-page="paginationOptions?.itemsPerPage"
          :loading="globalSettings.isLoading"
          hover
          @update:options="fetchData"
          :style="{
            background: `${useTheme().current.value.colors.background}`
          }"
      >
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <template v-slot:top>
          <v-row
              align="center"
              justify="space-between"
              :style="{
                background: `linear-gradient(to bottom, ${useTheme().current.value.colors.primary} -50%, ${useTheme().current.value.colors.background} 40%)`
              }"
              class="pa-2 pa-md-4"
          >
            <v-col cols="12" md="2">
              <v-toolbar-title class="d-flex justify-start">
                <v-icon color="medium-emphasis" :icon="icon" size="x-small" start></v-icon>
                {{ title }}
              </v-toolbar-title>
            </v-col>
            <v-col cols="12" md="10">
              <AssetSearch
                  v-model="searchTerms"
                  :asset-keys="assetKey"
                  :items="assetList"
                  :fetch-items="assetFetcher"
                  :placeholder="assetPlaceholder"
                  :on-setting="openFilter"
                  :on-submit="fetchData"
                  @keydown.enter="fetchData"
                  inputmode="text"
                  enterkeyhint="done"
              />
            </v-col>
          </v-row>
        </template>

        <!--template v-if="!globalSettings.isMobile && headers.some(header => header.key == 'id')" v-slot:item.id="{item}">
          <v-chip :text="item.id" border="thin opacity-25" class="hover_color_pro-hellblau" label
                  prepend-icon="mdi-book" @click="openDialog(item.id ?? -1, item.orderId ?? -1)">
            <template v-slot:prepend>
              <v-icon color="medium-emphasis"></v-icon>
            </template>
          </v-chip>
        </template>
        <template v-else-if="!globalSettings.isMobile && headers.some(header => header.key == 'assetId')" v-slot:item.assetId="{item}">
          <v-chip :text="item.assetId" border="thin opacity-25" class="hover_color_pro-hellblau" label
                  prepend-icon="mdi-book" @click="openDriveInformationDialog(item.assetId)">
            <template v-slot:prepend>
              <v-icon color="medium-emphasis"></v-icon>
            </template>
          </v-chip>
        </template-->

        <template v-if="globalSettings.isMobile" v-slot:headers>
        </template>

        <template v-if="!globalSettings.isMobile" v-slot:thead>
          <thead>
          <tr>
            <th v-for="header in props.displayHeader" :key="header.key" class="pa-2">
              <component
                  v-if="header.filterComponent"
                  :is="header.filterComponent"
                  v-model="searchTerms[header.key]"
                  v-bind="header.filterProps"
                  @keydown.enter="fetchData"
              />
              <v-text-field
                  v-else
                  v-model="searchTerms[header.key]"
                  :label="`in ${header.title} suchen...`"
                  dense
                  density="compact"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  style="width: 100%"
                  :variant="globalSettings.globalTextVariant"
                  @keydown.enter="fetchData"
                  :disabled="header.disabled"
              />
            </th>
          </tr>
          </thead>
        </template>

        <template
            v-for="(_, slot) in $slots"
            v-slot:[slot]="scope"
            :key="slot"
        >
          <slot :name="slot" v-bind="scope"/>
        </template>

        <template v-slot:no-data>
          <span>Keine Ergebnisse gefunden!</span>
          <v-btn
              border
              prepend-icon="mdi-backup-restore"
              rounded="lg"
              text="Neu Laden"
              variant="text"
              @click="fetchData"
          ></v-btn>
        </template>
      </v-data-table-server>
    </v-locale-provider>
  </v-sheet>
</template>
<script lang="ts" setup>
import {openFilterDialog, openOrderInformationDialog, openTaskInformationDialog, useGlobalSetting} from "@/lib/utils";
import AssetSearch from "@/components/inputs/AssetSearch.vue";
import {useTheme} from "vuetify/framework";
import {AssetFetcher, AssetItem} from "@/types/Asset";
import {SortItem} from "vuetify/lib/components/VDataTable/composables/sort";
import {Component, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";

export interface SearchTerm {
  id: string,
  title: string,
  assignee: string,
}

/*export type Header<T = Record<string, any>> = {
  key: 'data-table-group' | 'data-table-select' | 'data-table-expand' | (string & {});
  value?: SelectItemKey<T>;
  title?: string;
  fixed?: boolean;
  align?: 'start' | 'end' | 'center';
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  nowrap?: boolean;
  headerProps?: Record<string, any>;
  sortable?: boolean;
  filter?: FilterFunction;
  disabled?: boolean;
  canBeFiltered?: boolean;
  icon?: string;
  checkbox?: boolean;
  tab?: string;
  custom?: boolean;
  filterComponent?: Component;
  filterProps?: Record<string, any>;

  displayComponent?: Component;
  displayProps?: Record<string, any>;
};*/

export interface PaginationOptions {
  page: number;
  itemsPerPage: number;
}

const searchTerms = defineModel('searchTerm');
const paginationOptions = defineModel<PaginationOptions>('paginationOptions');
const sortBy = defineModel<SortItem[]>('sortOptions');

interface Props {
  headers: any[],
  displayHeader: any[],
  fetchData: () => Promise<void>,
  title: string,
  icon?: string
  assetList?: AssetItem[],
  assetFetcher?: AssetFetcher,
  list: any[],
  totalItems: number,
  assetKey: string | string[],
  assetPlaceholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  icon: "mdi-factory",
  assetKey: "title",
})

const globalSettings = useGlobalSetting();

const route = useRoute()
const router = useRouter()
const isReady = ref(false)

//TODO: synct nicht mit filter menu
function syncFiltersToUrl() {
  const cleanFilters = Object.fromEntries(
      Object.entries(searchTerms.value as Record<string, any>)
          .filter(([_, v]) => v !== '' && v != null)
  )
  router.replace({query: cleanFilters})
}

// Lokale fetchData überschattet props.fetchData
// Template verwendet automatisch diese Funktion
const fetchData = async () => {
  if (!isReady.value) return
  syncFiltersToUrl()
  await props.fetchData()
}

onMounted(async () => {
  if (Object.keys(route.query).length > 0) {
    Object.assign(searchTerms.value, route.query)
  }
  isReady.value = true
  await props.fetchData()  // Hier direkt props.fetchData, URL ist schon aus Query
})


function openDialog(id: number, orderId: number): void {
  if (props.headers.some(header => header.key == 'id') && props.headers.some(header => header.key == 'orderId')) {
    openTaskInformationDialog(orderId, id)
  } else {
    openOrderInformationDialog(id)
  }
}

function openFilter() {
  openFilterDialog(searchTerms, sortBy, fetchData, props.headers);
}

function handleBlur() {
  if (globalSettings.isMobile) {
    props.fetchData();
  }
}

</script>